import type { AccommodationType } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/accommodation-type";
import { accommodationTypeAndSubTypeIdFactory } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/accommodation-type-and-sub-type-id";
import { accommodationTypeIdFactory } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/accommodation-type-id";
import { pickedAccommodationSubTypeFactory } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-sub-type";
import type {
    PickedAccommodationType} from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-type";
import {
    pickedAccommodationTypeFactory,
} from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-type";
import { asDate } from "@@/shared/date-time/as-date";
import type { DateRange } from "@@/shared/date-time/date-range";
import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixFactory,
} from "@@/shared/date-time/iso-and-unix";
import type { OrderId } from "@@/shared/orders/order-id";
import {
    isOrderItemAccommodationRateItem_V2,
    isOrderItemAccommodation_V2,
} from "@@/shared/orders/order-items/is-order-item-v2";
import type { OrderProjection } from "@@/shared/orders/order-projections/main/order.projection";
import type { Price} from "@@/shared/prices/price";
import { priceFactory, zeroPrice } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { ScbMappingSettings } from "@@/shared/reports/accommodation-scb/scb-mapping-settings";
import { translation } from "@@/shared/translations/translation";
import { repeatIndex } from "@@/utilities/repeat";
import { parsePhoneNumber } from "awesome-phonenumber";
import { isSameDay } from "date-fns";

const getAccommodationPrice = (order: OrderProjection): Price => {
    const orderItem = order.orderItems.find(isOrderItemAccommodation_V2);
    if (!orderItem) return zeroPrice;

    const rateItems = order.orderItems.filter(
        isOrderItemAccommodationRateItem_V2,
    );
    return sumPrices(rateItems.map(ri => ri.price));
};

const getBedsAndRooms = (
    order: OrderProjection,
): { beds: number; rooms: number } => {
    const orderItem = order.orderItems.find(isOrderItemAccommodation_V2);
    if (!orderItem) return { beds: 0, rooms: 0 };

    if (!orderItem.quantities) return { beds: 0, rooms: 0 };

    const adults =
        orderItem.quantities.find(
            q => q.type.type === "ADULT" || q.type.type === "GUEST",
        )?.quantity.value || 0;
    const children =
        orderItem.quantities.find(
            q => q.type.type === "CHILD" || q.type.type === "INFANT",
        )?.quantity.value || 0;

    return { beds: adults + children, rooms: orderItem.quantity.value };
};

const getCountryCode = (order: OrderProjection) => {
    const phone = order.contactInfo.phoneNumber;
    const parsed = parsePhoneNumber(phone);

    return parsed.regionCode ?? "SE";
};

const unknownType = pickedAccommodationTypeFactory({
    title: translation({
        sv: "Okänd",
    }),
    typeId: accommodationTypeIdFactory(),
});

function convertOrderToSCBFormat_CAMPING(
    order: OrderProjection,
    mapping: ScbMappingSettings,
    types: AccommodationType[],
): ScBCampingData[] | undefined {
    const orderItem = order.orderItems.find(isOrderItemAccommodation_V2);
    if (!orderItem) return undefined;

    if (!orderItem.quantities) return undefined;

    const numberOfNights = Math.ceil(orderItem.time.duration / (60 * 60 * 24));

    const adults =
        orderItem.quantities.find(
            q => q.type.type === "ADULT" || q.type.type === "GUEST",
        )?.quantity.value || 0;
    const children =
        orderItem.quantities.find(
            q => q.type.type === "CHILD" || q.type.type === "INFANT",
        )?.quantity.value || 0;

    const homeCountry = getCountryCode(order);
    const ratePrice = getAccommodationPrice(order);

    if (order.accommodationSubTypeCount) {
        const type = types.find(
            t => t._id === orderItem?.accommodation.type.typeId,
        );
        // Get the subtype count and split them into individual bookings
        const data = Object.entries(order.accommodationSubTypeCount).flatMap(
            ([typeId, _count]) => {
                const subType = type?.subTypes?.find(
                    st =>
                        accommodationTypeAndSubTypeIdFactory(
                            type._id,
                            st._id,
                        ) === typeId,
                );

                const code = mapping.mappings[subType?._id ?? type?._id ?? ""];
                // Split the subtypes by count as well, could possibly be done in a more efficient way but this is the simplest
                return repeatIndex(_count).map(_i => {
                    return {
                        typeId: type?._id,
                        subTypeId: subType?._id,
                        orderId: order._id,
                        numberOfNights: numberOfNights,
                        adults: Math.ceil(adults / orderItem.quantity.value),
                        children: Math.ceil(
                            children / orderItem.quantity.value,
                        ),
                        accommodationType: type
                            ? pickedAccommodationTypeFactory({
                                  typeId: type._id,
                                  title: type.title,
                                  iconName: type.iconName,
                                  shortDescription: type.shortDescription,
                                  subType: subType
                                      ? pickedAccommodationSubTypeFactory({
                                            iconName: subType.iconName,
                                            title: subType.title,
                                            typeId: subType._id,
                                            shortDescription:
                                                subType.shortDescription,
                                        })
                                      : undefined,
                              })
                            : unknownType,
                        homeCountry: homeCountry,
                        arrivalDate: orderItem.time.start,
                        departureDate: orderItem.time.end,

                        lodgingIncome: priceFactory({
                            amountIncludingVat:
                                ratePrice.amountIncludingVat /
                                orderItem.quantity.value,
                            vat: ratePrice.vat,
                        }),
                        scbTypeId: code ?? {
                            scbCode: "5",
                            scbName: "Okänd",
                        },
                    } as ScBCampingData;
                });
            },
        );
        return data;
    } else {
        //Old way of handling before 2024-05-30
        const code =
            mapping.mappings[
                orderItem?.accommodation.type?.subType?.typeId ??
                    orderItem?.accommodation.type?.typeId
            ];
        const data = {
            orderId: order._id,
            numberOfNights: numberOfNights,
            adults: adults,
            children: children,
            accommodationType: orderItem?.accommodation?.type ?? unknownType,
            homeCountry: homeCountry,
            arrivalDate: orderItem.time.start,
            departureDate: orderItem.time.end,

            lodgingIncome: ratePrice,
            scbTypeId: code ?? {
                scbCode: "5",
                scbName: "Okänd",
            },
        } as ScBCampingData;
        return [data];
    }
}

const generate_ScBCampingReport = (params: {
    orders: OrderProjection[];
    range: DateRange;
    mapping: ScbMappingSettings;
    campsiteId: string;
    types: AccommodationType[];
}): ScBCampingReport => {
    const orders = params.orders;
    const campsiteId = params.campsiteId; //
    const bookings = orders.flatMap(order => {
        const data = convertOrderToSCBFormat_CAMPING(
            order,
            params.mapping,
            params.types,
        );
        if (!data) return [];
        return data;
    });

    return {
        bookings: bookings,
        campsiteId: campsiteId,
    };
};

type ScBCampingReport = {
    bookings: ScBCampingData[];
    campsiteId: string;
};

type ScBCampingData = {
    orderId: OrderId;

    numberOfNights: number;
    scbTypeId: {
        scbCode: string;
        scbName: string;
    };
    adults: number;
    children: number;
    accommodationType: PickedAccommodationType;
    homeCountry: string;
    arrivalDate: IsoAndUnixTimestamp;
    departureDate: IsoAndUnixTimestamp;

    lodgingIncome: Price;

    typeId?: string;
    subTypeId?: string;
};
const generate_ScBHotelReport = (params: {
    orders: OrderProjection[];
    range: DateRange;
}) => {
    const orders = params.orders;

    const amountPerDay = {} as {
        [date: string]: ScBHotelsVillagesHostelsDayData;
    };

    const start = asDate(params.range.start);
    const end = asDate(params.range.end);

    // Example capacity values,
    const totalPhysicalCapacityRooms = -1;
    const totalPhysicalCapacityBeds = -1;
    const availableCapacityRooms = -1;
    const availableCapacityBeds = -1;

    orders.forEach(order => {
        const homeCountry = getCountryCode(order); // Implement this based on your order structure
        const ratePrice = getAccommodationPrice(order); // Implement this based on your order structure
        const logiIncome = ratePrice.amountExcludingVat;

        const { beds, rooms } = getBedsAndRooms(order);

        order.orderItems.forEach(item => {
            if (!isOrderItemAccommodation_V2(item)) return;

            const startDate = asDate(item.time.start); // Implement this based on your order structure
            const nights = Math.ceil(item.time.duration / (60 * 60 * 24));

            for (let i = 0; i < nights; i++) {
                const currentDate = new Date(
                    startDate.getTime() + i * (1000 * 60 * 60 * 24),
                );

                if (currentDate < start) continue;
                if (currentDate > end) continue;

                const formattedDate = `${currentDate.getFullYear()}${(currentDate.getMonth() + 1).toString().padStart(2, "0")}${currentDate.getDate().toString().padStart(2, "0")}`;

                if (!amountPerDay[formattedDate]) {
                    amountPerDay[formattedDate] = {
                        date: isoAndUnixFactory(currentDate),
                        totals: {
                            arrivals: 0,
                            occupiedRooms: 0,
                            occupiedBeds: 0,
                            logiIncome: 0,
                            businessTravelers: 0,
                            conferenceGuests: 0,
                            groups: 0,
                            leisure: 0,
                        },
                        amountCountry: {}, // Using an object instead of a Map
                    };
                }

                const dayData = amountPerDay[formattedDate];
                dayData.totals.arrivals += isSameDay(currentDate, startDate)
                    ? beds
                    : 0;
                dayData.totals.logiIncome += logiIncome / nights;

                dayData.totals.occupiedRooms += rooms;
                dayData.totals.occupiedBeds += beds;

                dayData.totals.leisure += rooms; // Assuming leisure purpose

                if (!dayData.amountCountry[homeCountry]) {
                    dayData.amountCountry[homeCountry] = {
                        countryCode: homeCountry,
                        arrivals: 0,
                        occupiedRooms: 0,
                        occupiedBeds: 0,
                        logiIncome: 0,
                        businessTravelers: 0,
                        conferenceGuests: 0,
                        groups: 0,
                        leisure: 0,
                    };
                }

                const countryData = dayData.amountCountry[homeCountry];
                countryData.arrivals += isSameDay(currentDate, startDate)
                    ? beds //1
                    : 0;
                countryData.logiIncome += logiIncome / nights;

                countryData.occupiedRooms += rooms;
                countryData.occupiedBeds += beds;

                countryData.leisure += rooms; // Assuming leisure purpose
            }
        });
    });

    const result = {
        range: params.range,
        amountPerDay, // This is now a plain object
        totalPhysicalCapacityRooms,
        totalPhysicalCapacityBeds,
        availableCapacityRooms,
        availableCapacityBeds,
    };

    return result;
};

type ScBHotelsVillagesHostelsReport = {
    range: DateRange;

    amountPerDay: { [date: string]: ScBHotelsVillagesHostelsDayData };

    totalPhysicalCapacityRooms: number;
    totalPhysicalCapacityBeds: number;
    availableCapacityRooms: number;
    availableCapacityBeds: number;
};

type ScBHotelsVillagesHostelsDayData = {
    date: IsoAndUnixTimestamp;
    totals: {
        arrivals: number;
        occupiedRooms: number;
        occupiedBeds: number;
        logiIncome: number;
        businessTravelers: number;
        conferenceGuests: number;
        groups: number;
        leisure: number;
    };
    amountCountry: {
        [countryCode: string]: ScBHotelsVillagesHostelsDayCountryData;
    };
};

type ScBHotelsVillagesHostelsDayCountryData = {
    countryCode: string;
    arrivals: number;
    occupiedRooms: number;
    occupiedBeds: number;
    logiIncome: number;
    businessTravelers: number;
    conferenceGuests: number;
    groups: number;
    leisure: number;
};

const scbService = {
    hotelsReport: generate_ScBHotelReport,
    campingBookingReport: generate_ScBCampingReport,
};

export { scbService };
export type { ScBCampingReport, ScBHotelsVillagesHostelsReport };
